import { defineQuery } from 'next-sanity';

export const metaDataSettingsQuery = defineQuery(`*[_type == "settings"][0]{
  _id,
  metadataSettings
  }
`);

export const footerSettingsQuery = defineQuery(`*[_type == "settings"][0]{
    _id,
    footerSettings
    }
    `);

export const manufacturersHomePageQuery = defineQuery(`*[_type == "manufacturer"] | order(name asc) [0...14]{
  _id,
  name,
  slug,
  logo, 
}`);

export const footerManufacturersQuery = defineQuery(`*[_type == "manufacturer"] | order(name asc) [0...5]{
  _id,
  name,
  slug,
}`);

export const manufacturerHomePageCountQuery = defineQuery(`count(*[_type == "manufacturer"])`);

export const boatActivitiesHomePageQuery = defineQuery(
  `*[_type == "boatActivity" && count(*[_type == "boat" && references(^._id)]) > 0] | order(name asc) [0...14]{
    _id,
    name,
    slug,
    coverImage, 
  }`,
);

export const minimalFeaturesQuery = defineQuery(`*[_type == "featuresPages"] | order(order asc, title asc) [0...100]{
  _id,
  _createdAt,
  icon,
  slug,
  title,
}`);

export const allFeaturesQuery = defineQuery(`*[_type == "featuresPages"] | order(order asc, title asc){
  _id,
  description,
  image,
  slug,
  title,
  subtitle,
}`);

export const singleFeaturePageQuery = defineQuery(`*[_type == "featuresPages" && slug.current == $slug][0]{
  _id,
  description,
  image,
  slug,
  title,
  subtitle,
  icon,
  youtubeLink,
  featureList,
}`);

export const allFeaturesSlugQuery = defineQuery(`*[_type == "featuresPages"]{
  slug,
  _updatedAt
}`);

export const changelogsPageQuery = defineQuery(`*[_type == "changelog" ] | order(releaseDate desc, _createdAt desc) {
  _id,
  title,
  version,
  releaseDate,
  description,
}[$from...$to]`);

export const changelogsCountQuery = defineQuery(`count(*[_type == "changelog"])`);

export const blogHomePageQuery =
  defineQuery(`*[_type == "post"] | order(defined(date) desc, date desc, _createdAt desc) [$from...$to]{
  _id,
  _type,
  _createdAt,
  _updatedAt,
  _rev,
  title,
  slug,
  content,
  excerpt,
  coverImage,
  date,
  "categories": categories[]-> {
    _id,
    _type,
    title,
    slug
  },
  seo
}`);

export const blogPostsCountQuery = defineQuery(`count(*[_type == "post"])`);

export const blogRecentPostsQuery =
  defineQuery(`*[_type == "post"] | order(defined(date) desc, date desc, _createdAt desc) [0...5]{
  _id,
  coverImage,
  slug,
  title
}`);

export const allCategoriesQuery = defineQuery(
  `*[_type == "postCategory" && count(*[_type == "post" && references(^._id)]) > 0]`,
);

export const allCategoriesSlugsQuery = defineQuery(`*[
  _type == "postCategory" &&
  defined(slug.current) &&
  count(*[_type == "post" && references(^._id)]) > 0
]{
  slug,
  _updatedAt,
  "postsLastUpdated": *[_type == "post" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);

export const blogSearchQuery = defineQuery(`*[_type == "post" && (
  title match $searchTerm + "*" ||
  excerpt match $searchTerm + "*" ||
  pt::text(content) match $searchTerm + "*"
)] | order(defined(date) desc, date desc, _createdAt desc) [$from...$to]{
  _id,
  _type,
  _createdAt,
  _updatedAt,
  _rev,
  title,
  slug,
  content,
  excerpt,
  coverImage,
  date,
  "categories": categories[]-> {
    _id,
    _type,
    title,
    slug
  },
  seo
}`);

export const blogSearchCountQuery = defineQuery(`count(*[_type == "post" && (
  title match $searchTerm + "*" ||
  excerpt match $searchTerm + "*" ||
  pt::text(content) match $searchTerm + "*"
)])`);

export const singlePostQuery = defineQuery(`*[_type == "post" && slug.current == $slug][0]{
  _id,
  _type,
  _createdAt,
  _updatedAt,
  _rev,
  title,
  slug,
  content,
  excerpt,
  coverImage,
  date,
  "categories": categories[]-> {
    _id,
    _type,
    title,
    slug
  },
  seo
}`);

export const allPostSlugsQuery = defineQuery(`*[_type == "post" && defined(slug.current)]{
  "slug": slug.current,
  _updatedAt
}`);

export const similarPostsQuery =
  defineQuery(`*[_type == "post" && _id != $postId && count((categories[]._ref)[@ in $categoryIds]) > 0] | order(defined(date) desc, date desc, _createdAt desc) [0...4]{
  _id,
  _type,
  _createdAt,
  _updatedAt,
  _rev,
  title,
  slug,
  content,
  excerpt,
  coverImage,
  date,
  "categories": categories[]-> {
    _id,
    _type,
    title,
    slug
  },
  seo
}`);

export const categoryBySlugQuery = defineQuery(`*[_type == "postCategory" && slug.current == $slug][0]{
  _id,
  title,
  slug
}`);

export const blogPostsByCategoryQuery =
  defineQuery(`*[_type == "post" && references(*[_type=="postCategory" && slug.current == $categorySlug][0]._id)] | order(defined(date) desc, date desc, _createdAt desc) [$from...$to]{
  _id,
  _type,
  _createdAt,
  _updatedAt,
  _rev,
  title,
  slug,
  content,
  excerpt,
  coverImage,
  date,
  "categories": categories[]-> {
    _id,
    _type,
    title,
    slug
  },
  seo
}`);

export const blogPostsByCategoryCountQuery = defineQuery(
  `count(*[_type == "post" && references(*[_type=="postCategory" && slug.current == $categorySlug][0]._id)])`,
);

export const homepagePostCardsQuery =
  defineQuery(`*[_type == "post"] | order(defined(date) desc, date desc, _createdAt desc) [0...10]{
  _id,
  coverImage,
  slug,
  title,
  categories[]->{_id, title},
  excerpt
}`);

/**
 * Every spec field of a boat, with the references the screens that read it dereference.
 *
 * `...` first so the scalars ride along, then each reference is re-projected over the bare `_ref`
 * it would otherwise return. `referencePrice.currency` is flattened to its symbol, which is the
 * only part of a currency document anything prints.
 *
 * Shared by the boat detail page and the compare table, so the two cannot drift into disagreeing
 * about what a boat is.
 */
const BOAT_SPEC_PROJECTION = `
  ...,
  manufacturer->{_id, name, slug, website},
  type->{_id, name, slug},
  hullType->{_id, name, slug},
  hullMaterial->{_id, name, slug},
  categories[]->{_id, name, slug},
  activities[]->{_id, name, slug},
  designers[]->{_id, name, website},
  interiorDesigners[]->{_id, name, website},
  exteriorDesigners[]->{_id, name, website},
  referencePrice{
    amount,
    "currency": currency->symbol,
  }`;

/**
 * `documents[defined(@)]` guards against null entries in the array: 42 boats carry them, and they
 * used to reach the UI and throw while rendering, which the layout's Suspense boundary swallowed
 * into a silently blank page. Prerendering turns that into a hard build failure, so they are
 * filtered at source.
 *
 * The slug is matched case-insensitively because 64 boats are stored with uppercase in their slug
 * (`amels-altair-III-1974`, `crn-5G-2011`) while `proxy.ts` only ever serves the lowercase spelling
 * — an exact match left all 64 pages 404ing (MDC-4221). The exact comparison is kept as the first
 * clause so the ~6,000 already-lowercase slugs still resolve through the index rather than a scan.
 */
export const boatBySlugQuery =
  defineQuery(`*[_type == "boat" && (slug.current == $slug || lower(slug.current) == lower($slug))][0]{${BOAT_SPEC_PROJECTION},
  documents[defined(@)]{
    ...,
    file{
      asset->{ _id, url, size, extension, originalFilename }
    }
  }
}`);

/**
 * The boats currently in Compare, by the only thing the compare store keeps of them: their `_id`.
 *
 * The same spec sheet the detail page reads, because the compare table prints the same fields.
 * It used to be assembled from the Meilisearch hit that added the boat, which no longer carries
 * any of it — the index is card-sized on purpose (a picture, a name, a maker, a year) and is not
 * going to hold a copy of the catalogue again.
 *
 * `documents` is left out: those are the boat's PDFs, and compare shows no downloads.
 */
export const compareBoatsQuery = defineQuery(`*[_type == "boat" && _id in $ids]{${BOAT_SPEC_PROJECTION}
}`);

export const allBoatSlugsQuery = defineQuery(`*[_type == "boat" && defined(slug.current)]{
  "slug": slug.current,
  _updatedAt
}`);

export const boatsByActivityQuery = defineQuery(`*[_type == "boatActivity" && slug.current == $slug][0]{
  _id,
  name,
  description,
  slug,
  coverImage,
  seo,
  "boats": *[_type == "boat" && references(^._id)] | order(
    select($sort == "views" => coalesce(viewsCount, 0)) desc,
    select($sort == "recent" => _createdAt) desc,
    select($sort == "alphabetical" => model) asc
  ) [$from...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

/**
 * The boats behind a set of favorites, by Sanity document id.
 *
 * A favorite stores only `favoritable_ref` — the boat's `_id` — so the favourites page has ids and
 * no boat to render until this fills them in. The projection is the one every boat card is built
 * from, so the cards on this page are the same cards as everywhere else.
 *
 * Unordered on purpose: `in $ids` gives no meaningful order, and the caller re-sorts into the order
 * the favorites came back in (newest first), which is the order the page promises.
 */
export const favouriteBoatsQuery = defineQuery(`*[_type == "boat" && _id in $ids]{
  _id,
  _createdAt,
  model,
  yearFirst,
  slug,
  image,
  viewsCount,
  manufacturer->{_id, name, slug, website},
}`);

export const boatsByActivityCountQuery = defineQuery(
  `count(*[_type == "boat" && references(*[_type == "boatActivity" && slug.current == $slug][0]._id)])`,
);

export const allActivitiesBoatsQuery = defineQuery(`*[
  _type == "boatActivity" &&
  defined(slug.current)
] | order(name asc){
  _id,
  name,
  slug,
  "boats": *[_type == "boat" && references(^._id)] | order(coalesce(viewsCount, 0) desc) [0...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const allActivitiesSlugsQuery = defineQuery(`*[
  _type == "boatActivity" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
]{
  "slug": slug.current,
  _updatedAt,
  "boatsLastUpdated": *[_type == "boat" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);

export const boatsByCategoryQuery = defineQuery(`*[_type == "boatCategory" && slug.current == $slug][0]{
  _id,
  name,
  description,
  slug,
  coverImage,
  seo,
  "boats": *[_type == "boat" && references(^._id)] | order(
    select($sort == "views" => coalesce(viewsCount, 0)) desc,
    select($sort == "recent" => _createdAt) desc,
    select($sort == "alphabetical" => model) asc
  ) [$from...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const boatsByCategoryCountQuery = defineQuery(
  `count(*[_type == "boat" && references(*[_type == "boatCategory" && slug.current == $slug][0]._id)])`,
);

// The taxonomy listings and the sitemap/static-param queries all guard on `defined(slug.current)`:
// the schema marks slug required, but imported documents can still land without one, and every
// consumer builds a link out of it. An unslugged category has no page to link to, so it is left out
// rather than rendered as a dead `/undefined` URL.
export const allCategoriesBoatsQuery = defineQuery(`*[
  _type == "boatCategory" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
] | order(name asc){
  _id,
  name,
  slug,
  "boats": *[_type == "boat" && references(^._id)] | order(coalesce(viewsCount, 0) desc) [0...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const allBoatCategoriesSlugsQuery = defineQuery(`*[
  _type == "boatCategory" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
]{
  "slug": slug.current,
  _updatedAt,
  "boatsLastUpdated": *[_type == "boat" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);

export const boatsByTypeQuery = defineQuery(`*[_type == "boatType" && slug.current == $slug][0]{
  _id,
  name,
  description,
  slug,
  coverImage,
  seo,
  "boats": *[_type == "boat" && references(^._id)] | order(
    select($sort == "views" => coalesce(viewsCount, 0)) desc,
    select($sort == "recent" => _createdAt) desc,
    select($sort == "alphabetical" => model) asc
  ) [$from...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const boatsByTypeCountQuery = defineQuery(
  `count(*[_type == "boat" && references(*[_type == "boatType" && slug.current == $slug][0]._id)])`,
);

export const allTypesBoatsQuery = defineQuery(`*[
  _type == "boatType" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
] | order(name asc){
  _id,
  name,
  slug,
  "boats": *[_type == "boat" && references(^._id)] | order(coalesce(viewsCount, 0) desc) [0...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const allBoatTypesSlugsQuery = defineQuery(`*[
  _type == "boatType" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
]{
  "slug": slug.current,
  _updatedAt,
  "boatsLastUpdated": *[_type == "boat" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);

export const boatsByHullTypeQuery = defineQuery(`*[_type == "boatHullType" && slug.current == $slug][0]{
  _id,
  name,
  description,
  slug,
  coverImage,
  seo,
  "boats": *[_type == "boat" && references(^._id)] | order(
    select($sort == "views" => coalesce(viewsCount, 0)) desc,
    select($sort == "recent" => _createdAt) desc,
    select($sort == "alphabetical" => model) asc
  ) [$from...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const boatsByHullTypeCountQuery = defineQuery(
  `count(*[_type == "boat" && references(*[_type == "boatHullType" && slug.current == $slug][0]._id)])`,
);

export const allHullTypesBoatsQuery = defineQuery(`*[
  _type == "boatHullType" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
] | order(name asc){
  _id,
  name,
  slug,
  "boats": *[_type == "boat" && references(^._id)] | order(coalesce(viewsCount, 0) desc) [0...$to]{
    _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const allManufacturersQuery = defineQuery(`*[_type == "manufacturer"] | order(name asc)[0...1000]`);

export const manufacturerBySlugQuery = defineQuery(`*[_type == "manufacturer" && $slug == slug.current][0]{
  ...,
  "boats": *[_type == "boat" && references(^._id)] | order(
    select($sort == "views" => coalesce(viewsCount, 0)) desc,
    select($sort == "recent" => _createdAt) desc,
    select($sort == "alphabetical" => model) asc
  ) [$from...$to]{
     _id,
    _createdAt,
    model,
    yearFirst,
    slug,
    image,
    viewsCount,
    manufacturer->{_id, name, slug, website},
  }
}`);

export const manufacturerBoatsCountQuery = defineQuery(
  `count(*[_type == "boat" && references(*[_type == "manufacturer" && slug.current == $slug][0]._id)])`,
);

// `boatsLastUpdated` is projected because a maker page's content is its boat list, not the
// manufacturer document — a maker record untouched for years still changes whenever one of its
// boats does. The sitemap reports whichever of the two is more recent.
export const allManufacturersSlugsQuery = defineQuery(`*[
  _type == "manufacturer" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
]{
  "slug": slug.current,
  _updatedAt,
  "boatsLastUpdated": *[_type == "boat" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);

export const allBoatHullTypesSlugsQuery = defineQuery(`*[
  _type == "boatHullType" &&
  defined(slug.current) &&
  count(*[_type == "boat" && references(^._id)]) > 0
]{
  "slug": slug.current,
  _updatedAt,
  "boatsLastUpdated": *[_type == "boat" && references(^._id)] | order(_updatedAt desc)[0]._updatedAt
}`);
